import { MaterialityAssessmentScope, useGetAssessmentSizeQuery } from '../../api/materiality-assessment';
import { useRegenerateMetricGroup } from './hooks/useRegenerateMetricGroup';
import { topicLengthMap } from '@apps/materiality-tracker/utils';
import { ROUTES } from '@constants/routes';
import { PACK, QUESTION } from '@constants/terminology';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { skipToken } from '@reduxjs/toolkit/query';
import { generateUrl } from '@routes/util';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';

const getTopicsCountOptions = (scope: MaterialityAssessmentScope | undefined) => {
  if (!scope) {
    return [];
  }
  const maxTopicLength = topicLengthMap[scope];
  const result = [];
  // generate 5, 10, 15, ... to maxTopicLength, based on assessment's size scope
  for (let i = 5; i <= maxTopicLength; i += 5) {
    result.push({ label: `Top ${i} topics`, value: i });
  }
  return result;
};

const getDefaultTopicsCount = (topTopicCounts: number | undefined, scope: MaterialityAssessmentScope | undefined) => {
  if (topTopicCounts) {
    return topTopicCounts;
  }
  return scope ? topicLengthMap[scope] : undefined;
};

export const MetricGroupConfiguration = ({ metricGroup }: { metricGroup: MetricGroup }) => {
  const history = useHistory();
  const initiativeId = metricGroup.initiativeId;
  const assessmentId = metricGroup.survey?._id;
  const currentTopTopicsCount = metricGroup.source?.topTopicsCount;

  const { data, isFetching: isLoadingAssessmentSize } = useGetAssessmentSizeQuery(
    initiativeId && assessmentId ? { initiativeId, assessmentId } : skipToken,
  );

  const options = getTopicsCountOptions(data?.sizeScope);

  const [topTopicsCount, setTopTopicsCount] = useState<number | undefined>(undefined);

  const { handleRegenerate } = useRegenerateMetricGroup();

  useEffect(() => {
    setTopTopicsCount(getDefaultTopicsCount(currentTopTopicsCount, data?.sizeScope));
  }, [data?.sizeScope, currentTopTopicsCount]);

  const hasChanged = currentTopTopicsCount !== topTopicsCount;

  return (
    <div>
      {isLoadingAssessmentSize ? <BlockingLoader /> : null}
      <div className='fw-semibold'>{PACK.CAPITALIZED_SINGULAR} scope</div>
      <div className='mb-2'>
        Which segment of topics would you like to use to generate {QUESTION.PLURAL} for this custom {PACK.SINGULAR}:
      </div>
      <SelectFactory
        selectType={SelectTypes.SingleSelect}
        options={options}
        value={options.find((option) => option.value === topTopicsCount)}
        onChange={(option) => (option ? setTopTopicsCount(option.value) : undefined)}
        isSearchable={false}
      />
      <div className='text-right mt-3'>
        <Button
          color='link-secondary mr-3'
          onClick={() => history.push(generateUrl(ROUTES.MATERIALITY_TRACKER_MODULES, { initiativeId }))}
        >
          Cancel
        </Button>
        <Button color='primary' disabled={!hasChanged} onClick={() => handleRegenerate(metricGroup, topTopicsCount)}>
          Update assigned {QUESTION.PLURAL}
        </Button>
      </div>
    </div>
  );
};
